/**
 * @file Components/Sections/Blogs.jsx
 * @description Blog preview section for the home page. Displays recent blog
 * posts in a card grid with thumbnails, excerpts, and read-more links.
 */

import React from 'react';
import PropTypes from 'prop-types';

const Blogs = ({ blogs = [], title = "Latest Blogs" }) => {
  if (!blogs || blogs.length === 0) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {title}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No blog posts available at the moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Discover travel tips, destination guides, and inspiring stories
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(blogs || []).map((blog, index) => (
            <article
              key={index}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              {blog.image && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  {blog.category && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2">
                      {blog.category}
                    </span>
                  )}
                  {blog.date && (
                    <span>{new Date(blog.date).toLocaleDateString()}</span>
                  )}
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 hover:text-blue-600 transition-colors duration-200">
                  <a href={blog.link || '#'}>
                    {blog.title}
                  </a>
                </h3>
                
                {blog.excerpt && (
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  {blog.author && (
                    <div className="flex items-center">
                      {blog.author.avatar && (
                        <img
                          src={blog.author.avatar}
                          alt={blog.author.name}
                          className="w-8 h-8 rounded-full mr-2"
                        />
                      )}
                      <span className="text-sm text-gray-600">
                        {blog.author.name || blog.author}
                      </span>
                    </div>
                  )}
                  
                  <a
                    href={blog.link || '#'}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200"
                  >
                    Read More →
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 transition-colors duration-200">
            View All Blogs
          </button>
        </div>
      </div>
    </div>
  );
};

Blogs.propTypes = {
  blogs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      excerpt: PropTypes.string,
      image: PropTypes.string,
      date: PropTypes.string,
      category: PropTypes.string,
      author: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          avatar: PropTypes.string
        })
      ]),
      link: PropTypes.string
    })
  ),
  title: PropTypes.string
};

export default Blogs;
